import React from 'react';
import { motion } from 'framer-motion';
import { appConfig } from '../config/appConfig';
import WhatsAppGroupButton from '../components/ui/WhatsAppGroupButton';
import KannadaTagline from '../components/ui/KannadaTagline';

const AboutPage: React.FC = () => {
  const { about } = appConfig;
  const photoSrc = about.image.replace(/^v1\/public/, '');
  
  // Debug: Check if locations exist
  console.log('appConfig.locations:', appConfig.locations);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen"
    >
      {/* Hero-style intro section */}
      <section className="bg-neutral-charcoal pt-28 pb-16">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <img
                src={photoSrc}
                alt={about.name}
                className="rounded-2xl shadow-2xl w-full max-w-sm object-cover"
                loading="eager"
                decoding="async"
              />
            </div>
            <div>
              <h1 className="heading-primary text-neutral-white">{about.name}</h1>
              <p className="body-large text-neutral-white/90 mt-4 whitespace-pre-line">
                {about.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed about section */}
      <section className="bg-neutral-white py-16">
        <div className="container max-w-4xl">
          <h2 className="heading-secondary text-center mb-8">About</h2>
          <div className="space-y-6 body-regular text-neutral-dark-gray">
            {about.about.split(/\n\n+/).map((paragraph, idx) => (
              <p key={idx}>{paragraph.trim()}</p>
            ))}
          </div>

          <div className="mt-12 pt-10 border-t border-neutral-medium-gray text-center">
            <KannadaTagline
              className="justify-center mb-6"
              imgClassName="h-20 sm:h-24 md:h-28 w-auto max-w-full rounded-lg shadow-lg"
            />
            <p className="body-large text-neutral-dark-gray max-w-2xl mx-auto">
              ಯಾವುದೇ ಪ್ರವಾಸ ನಮ್ಮ ಮಾತ್ರು ಭಾಷೆಯಲ್ಲಿದ್ದರೆ ನಮಗೆ ನಮ್ಮವರದೇ ಅನುಭವವಾಗುತ್ತದೆ. ಭಾರತದಲ್ಲಿ ಪ್ರತೀ ಪ್ರದೇಶದ ಜನರ ಪದ್ದತಿ, ಊಟ, ನಡವಳಿಕೆ ವಿಭಿನ್ನ ಆದ್ದರಿಂದಲೇ ನಾವು ಕೇವಲ ಕನ್ನಡಗರಿಗಾರಿ ಕನ್ನಡದಲ್ಲಿ ಪ್ರವಾಸಗಳನ್ನು ಆಯೋಜಿಸುತ್ತಿದ್ದೇವೆ. ನೀವು ಎಂದಿಗೂ ನಿಮ್ಮ ಮನೆಯನ್ನು ಬಿಟ್ಟ ಅನುಭವವಾಗುವುದಿಲ್ಲ.  ಕನ್ನಡದವರೊಂದಿಗೆ ಕನ್ನಡದಲ್ಲಿ ನಮ್ಮ ಪ್ರವಾಸಗಳು.
            </p>
          </div>
        </div>
      </section>

      {/* Our Locations section */}
      {appConfig.locations && appConfig.locations.length > 0 && (
        <section className="bg-neutral-light-gray py-16">
          <div className="container">
            <div className="text-center mb-12">
              <p className="body-large text-secondary-slate max-w-2xl mx-auto mb-6">
                Connect with our travel community for updates, tips, and support from fellow adventurers.
              </p>
              <WhatsAppGroupButton />
            </div>
            <h2 className="heading-secondary text-center mb-12">Our Locations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {appConfig.locations.map((location, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="group relative bg-neutral-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className="p-6 flex flex-col items-center">
                  <h3 className="text-center mb-4 text-neutral-charcoal font-semibold" style={{ fontSize: '0.875rem' }}>
                    {location.name}
                  </h3>
                  <div className="w-full h-32 flex items-center justify-center mb-4">
                    <img
                      src={location.logoPath}
                      alt={`${location.name} logo`}
                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                </div>

                {/* Hover overlay with details */}
                {(location.contactName || location.address || location.phone) && (
                  <div className="absolute inset-0 bg-neutral-charcoal/95 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-6">
                    <div className="text-neutral-white space-y-3">
                      {location.contactName && (
                        <div>
                          <p className="text-sm font-semibold text-brand-teal">Contact</p>
                          <p className="body-small">{location.contactName}</p>
                        </div>
                      )}
                      {location.address && (
                        <div>
                          <p className="text-sm font-semibold text-brand-teal">Address</p>
                          <p className="body-small">{location.address}</p>
                        </div>
                      )}
                      {location.phone && (
                        <div>
                          <p className="text-sm font-semibold text-brand-teal">Phone</p>
                          <p className="body-small">{location.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default AboutPage;


